import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import userLoginLogo from '../assets/UserLoginLogo.png';
import {
  MdDashboard,
  MdPerson,
  MdPeople,
  MdStraighten,
  MdLocalOffer,
  MdImage,
  MdSettings,
  MdInventory2,
  MdAssignment,
  MdCreditCard,
  MdTrendingUp,
  MdNotifications,
  MdClose,
  MdMenu,
} from 'react-icons/md';
import { FaFileAlt } from 'react-icons/fa';
import './AdminLayout.css';

const SCROLLBAR_VISIBLE_MS = 800;

const AdminLayout: React.FC = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const sidebarNavRef = useRef<HTMLElement>(null);
  const contentRef = useRef<HTMLElement>(null);
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastScrollbarElRef = useRef<HTMLElement | null>(null);

  const showScrollbar = (el: HTMLElement | null) => {
    if (!el) return;
    if (lastScrollbarElRef.current && lastScrollbarElRef.current !== el) {
      lastScrollbarElRef.current.classList.remove('show-scrollbar');
    }
    lastScrollbarElRef.current = el;
    el.classList.add('show-scrollbar');
    if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    scrollTimeoutRef.current = setTimeout(() => {
      el.classList.remove('show-scrollbar');
      if (lastScrollbarElRef.current === el) lastScrollbarElRef.current = null;
      scrollTimeoutRef.current = null;
    }, SCROLLBAR_VISIBLE_MS);
  };

  useEffect(() => {
    const sidebar = sidebarNavRef.current;
    const content = contentRef.current;
    const onCaptureScroll = (e: Event) => {
      const target = e.target as HTMLElement;
      if (target === sidebar || target?.classList?.contains('sidebar-nav')) {
        showScrollbar(sidebar);
      } else if (target === content || target?.classList?.contains('admin-content')) {
        showScrollbar(content);
      } else if (target?.closest?.('.admin-content')) {
        showScrollbar(target);
      } else if (target?.closest?.('.sidebar-nav')) {
        showScrollbar(sidebar);
      }
    };
    document.addEventListener('scroll', onCaptureScroll, true);
    return () => {
      document.removeEventListener('scroll', onCaptureScroll, true);
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
      if (lastScrollbarElRef.current) {
        lastScrollbarElRef.current.classList.remove('show-scrollbar');
      }
    };
  }, []);

  const goToDashboard = () => {
    navigate('/dashboard');
    setMenuOpen(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleNavClick = (path: string) => {
    navigate(path);
    setMenuOpen(false);
  };

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', Icon: MdDashboard },
    { path: '/users', label: 'Users', Icon: MdPerson },
    { path: '/distributors', label: 'Distributors', Icon: MdPeople },
    { path: '/liters', label: 'Liter Master', Icon: MdStraighten },
    { path: '/brands', label: 'Brand Master', Icon: MdLocalOffer },
    { path: '/banners', label: 'Banners', Icon: MdImage },
    { path: '/settings', label: 'Settings', Icon: MdSettings },
    { path: '/cms', label: 'CMS', Icon: FaFileAlt },
    { path: '/products', label: 'Products', Icon: MdInventory2 },
    { path: '/orders', label: 'Orders', Icon: MdAssignment },
    { path: '/payments', label: 'Payments', Icon: MdCreditCard },
    { path: '/reports', label: 'Reports', Icon: MdTrendingUp },
    { path: '/notifications', label: 'Notifications', Icon: MdNotifications },
  ];

  return (
    <div className="admin-layout">
      <div
        className={`admin-sidebar-overlay ${menuOpen ? 'open' : ''}`}
        onClick={() => setMenuOpen(false)}
        onKeyDown={(e) => e.key === 'Escape' && setMenuOpen(false)}
        role="button"
        tabIndex={0}
        aria-label="Close menu"
      />
      <aside className={`admin-sidebar ${menuOpen ? 'open' : ''}`}>
        <button type="button" className="sidebar-brand" onClick={goToDashboard}>
          <img src={userLoginLogo} alt="V-GAS" className="sidebar-logo" />
          <span className="sidebar-brand-text">V Gas Admin</span>
        </button>
        <button type="button" className="sidebar-close-btn" onClick={() => setMenuOpen(false)} aria-label="Close menu">
          <MdClose size={20} />
        </button>
        <nav className="sidebar-nav" ref={sidebarNavRef}>
          {navItems.map((item) => {
            const IconComponent = item.Icon as React.ComponentType<{ size?: number }>;
            return (
              <button
                key={item.path}
                type="button"
                className={`sidebar-link ${location.pathname === item.path ? 'active' : ''}`}
                onClick={() => handleNavClick(item.path)}
              >
                <span className="sidebar-icon">
                  <IconComponent size={22} />
                </span>
                <span>{item.label}</span>
              </button>
            );
          })}
        </nav>
      </aside>
      <div className="admin-main">
        <header className="admin-header">
          <button type="button" className="admin-menu-toggle" onClick={() => setMenuOpen(true)} aria-label="Open menu">
            <MdMenu size={24} className="admin-menu-toggle-icon" />
          </button>
          <h1 className="admin-header-title">
            {navItems.find((i) => i.path === location.pathname)?.label || 'Admin'}
          </h1>
          <button type="button" onClick={handleLogout} className="admin-logout-btn">
            Logout
          </button>
        </header>
        <main className="admin-content" ref={contentRef}>
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
